import React, { useEffect, useState } from 'react';
import './style.scss';
import { ProductCard } from 'views/components/molecules/product';
import { ContentHeader } from 'views/components/molecules/contentHeader';
import { IconColor, IconSize, SvgIcon } from 'views/components/atoms/svgIcon';
import { IconName, findIcon } from 'views/utils/handleIcon';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApplySymbolMutationVariables,
  GetSymbolInfoQueryVariables,
  useApplySymbolMutation,
} from 'views/utils/graphql/graphql';
import { useGetSymbolDetail } from './hooks/useGetSymbolDetail';
import { formatThousand } from 'views/utils/calc/formatThousand';
import { operationDatePlanCalc } from 'views/utils/calc/operationDatePlan';
import { ModalBase, ModalStyle } from 'views/components/atoms/modal';
import { Toast, ToastMessageLevel } from 'views/components/atoms/toast';
import { Button, ButtonSize, FillType } from 'views/components/atoms/button';
import { useToast } from 'views/components/atoms/toast/hook';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useGlobalLoadingHandler } from 'views/components/organisms/globalLoading/hook';

export const ProductDetail = () => {
  // Routerのパラメーターを取得する
  const { symbol } = useParams<{ symbol: string }>();

  const getNewSymbolDetailVariables = () => {
    const variables: GetSymbolInfoQueryVariables = {
      symbol: symbol,
    };
    return variables;
  };

  const { symbolDetail, setSymbolDetailVariables } = useGetSymbolDetail(getNewSymbolDetailVariables());
  const data = symbolDetail.data?.GetSymbolInfo || null;

  const [quantity, setQuantity] = useState<number>(1);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = parseInt(event.target.value);
    if (!isNaN(newAmount) && newAmount > 0) {
      setQuantity(newAmount);
    }
  };
  const handleDecrease = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };
  const handleIncrease = () => {
    setQuantity(quantity + 1);
  };

  const [isCheck, setIsCheck] = useState<boolean>(false);
  const [isCheck2, setIsCheck2] = useState<boolean>(false);

  const [isLinkOpen, setIsLinkOpen] = useState<boolean>(false);
  const [isLinkOpen2, setIsLinkOpen2] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOpen2, setIsOpen2] = useState<boolean>(false);

  const [applySymbolData, applySymbol] = useApplySymbolMutation();
  const applySymbolSubmit = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        if (!symbol) return;
        const param: ApplySymbolMutationVariables = {
          input: {
            Symbol: symbol,
            Quantity: quantity,
          },
        };
        await applySymbol(param);
      },
      isLoadingForceCenter: true,
    },
    [symbol, quantity, applySymbol],
  );

  const navigate = useNavigate();

  const { toastProps, showToast, hideToast } = useToast();

  const { showLoading, hideLoading, setLoadingCenter } = useGlobalLoadingHandler();

  useEffect(() => {
    setLoadingCenter(true);
    if (symbolDetail.fetching) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [symbolDetail]);

  useEffect(() => {
    if (applySymbolData.fetching) return;
    if (!applySymbolData.data) return;

    if (applySymbolData.data.ApplySymbol?.Code === 0) {
      setIsOpen(false);
      setIsOpen2(true);
    } else if (applySymbolData.data.ApplySymbol?.Code !== 0) {
      setIsOpen(false);
      const msgList = applySymbolData.data.ApplySymbol?.AdditionalMessage;
      const msg = msgList?.map((x, i) => (i < msgList.length - 1 ? '・' + x : x)).join('\n\n');
      showToast(msg ?? applySymbolData.data.ApplySymbol?.Message ?? '申込に失敗しました', ToastMessageLevel.Error);
    }
  }, [applySymbolData]);

  return (
    <section className="outletWrapper">
      <div className="centerContent">
        <ProductCard
          title={data?.SymbolName || ''}
          image={data?.ImageUrl || ''}
          TradeStartDate={data?.TradeStartDate || ''}
          TradeEndDate={data?.TradeEndDate || ''}
          description={data?.Overview || ''}
          details={[
            {
              title: '一口',
              item: formatThousand(data?.PricePerUnit || 0) || '',
              frontUnit: '',
              rearUnit: '万円',
            },
            {
              title: '予想分配率',
              item: data?.ExpectedInterestRate.toString() || '',
              frontUnit: '年',
              rearUnit: '%',
            },
            {
              title: '予定運用期間',
              item: operationDatePlanCalc(data?.OperationStartDate || '', data?.OperationEndDate || ''),
              frontUnit: '約',
              rearUnit: '',
            },
          ]}
          tradable={data?.Tradable || false}
        />
        <div className="detailWrapper">
          <ContentHeader title={'銘柄概要'} />
          <span className="detailWrapper_description">{data?.Explanation || ''}</span>
        </div>
      </div>
      <div className="rightContent">
        <div className="rightContent_wrapper">
          <ContentHeader title="申込口数" />
          <div className="requestCount">
            <button className="requestCount_spin sub" onClick={handleDecrease}>
              －
            </button>
            <input className="requestCount_counter" type="number" value={quantity} onChange={handleAmountChange} />
            <button className="requestCount_spin add" onClick={handleIncrease}>
              ＋
            </button>
          </div>
        </div>

        <div className="rightContent_wrapper">
          <ContentHeader title="申込金額" />
          <div className="requestAmount">
            <span className="requestAmount_value">{(quantity * (data?.PricePerUnit || 0)).toLocaleString()}</span>
            <span className="requestAmount_unit">円</span>
          </div>
        </div>

        {/* TODO:PDFのリンクをクリックしないとチェックボックスは押せない */}
        <div className="rightContent_wrapper">
          <ContentHeader title="確認事項" />
          <div className="requestMatter">
            <span className="requestMatter_description">
              ※「目論見書」及び「契約締結前交付書面」のURLをクリックすることでチェックできるようになります
            </span>
            <span className="requestMatter_description">
              ※書面のダウンロードが必要な場合はブラウザに表示した後、ブラウザの機能で保存可能です
            </span>
            <ConfirmCheck
              href={data?.ProspectusUrl || ''}
              pdfLabel="目論見書"
              unitLabel="を読み、その内容を理解しました"
              checked={isCheck}
              onChange={() => setIsCheck(!isCheck)}
              onPDFLinkOpen={() => setIsLinkOpen(true)}
            />
            <ConfirmCheck
              href={data?.PreContractDocumentUrl || ''}
              pdfLabel="契約締結前交付書面"
              unitLabel="を読み、その内容を理解しました"
              checked={isCheck2}
              onChange={() => setIsCheck2(!isCheck2)}
              onPDFLinkOpen={() => setIsLinkOpen2(true)}
            />
            <span className="requestMatter_description">説明が必要な場合には、お問い合わせ画面から連絡をください</span>
          </div>
        </div>

        <Button
          label={data?.Tradable ? '申込内容の確認へ' : '募集前のためお申込みできません'}
          onClick={() => {
            const targetPDFList = [];
            if (!isCheck || !isLinkOpen) {
              targetPDFList.push('目論見書');
            }
            if (!isCheck2 || !isLinkOpen2) {
              targetPDFList.push('契約締結前交付書面');
            }
            if (targetPDFList.length !== 0) {
              const pdfMsg = targetPDFList.join('・');
              showToast(`${pdfMsg}の内容を確認し、チェックを付けてください`, ToastMessageLevel.Error);
              return;
            }

            hideToast();
            setIsOpen(true);
          }}
          fillType={FillType.primaryFill}
          buttonSize={ButtonSize.sizeM}
          disabled={!data?.Tradable}
        />
      </div>
      <ModalBase
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        modalStyle={ModalStyle.confirmModal}
        children={
          <div className="modalWrapper">
            <section>
              <ContentHeader title="申込口数" />
              <div className="confirmItem">
                <span className="confirmItem_value">{quantity}</span>
                <span className="confirmItem_unit">口</span>
              </div>
              <ContentHeader title="申込金額" />
              <div className="confirmItem">
                <span className="confirmItem_value">{(quantity * (data?.PricePerUnit || 0)).toLocaleString()}</span>
                <span className="confirmItem_unit">円</span>
              </div>
            </section>

            <div className="confirmCaution">
              <div>＜ご注意＞</div>
              <div>・申込後の訂正、取消しはできません</div>
              <div>
                ・目論見書および契約締結前交付書面に記載の投資リスクなどにより、場合によっては投資金額の全額または一部を回収できないことがあります
              </div>
              <div>
                ・取得の申込をする金融商品は預金等ではなく、預金保険法第五十三条に規定する保険金の支払い（いわゆる「預金保険機構」による保険金の支払い）の対象とはなりません
              </div>
              <div>・抽選割り当ての結果によっては、受益証券を取得できない場合があります</div>
            </div>

            <Button
              label="内容を確認して申込む"
              onClick={applySymbolSubmit}
              fillType={FillType.primaryFill}
              buttonSize={ButtonSize.sizeM}
            />
          </div>
        }
      />
      <ModalBase
        isOpen={isOpen2}
        modalStyle={ModalStyle.completeModal}
        children={
          <div className="modalWrapper">
            <span className="finishLabel">申込が完了しました</span>
            <Button
              label="閉じる"
              onClick={() => {
                setIsOpen2(false);
                // productListへ遷移
                navigate('/productList');
              }}
              fillType={FillType.primaryFill}
              buttonSize={ButtonSize.sizeM}
            />
          </div>
        }
      />
      <Toast {...toastProps} />
    </section>
  );
};

// 銘柄詳細の申込前に利用
type ConfirmCheckProps = {
  href: string;
  pdfLabel: string;
  unitLabel: string;
  checked: boolean;
  onChange: () => void;
  onPDFLinkOpen: () => void;
};

export const ConfirmCheck = (props: ConfirmCheckProps) => {
  const { href, pdfLabel, unitLabel, checked, onChange, onPDFLinkOpen } = props;
  const icon = findIcon(IconName.PdfFile);
  return (
    <div className="confirmCheck">
      <input className="confirmCheck_button" type="checkbox" checked={checked} onChange={onChange} />
      <div className="confirmCheck_labels">
        <SvgIcon icon={icon} size={IconSize.icon_m} color={IconColor.gray} />
        <span>
          <a href={href} target="_blank" rel="noreferrer noopener" onClick={onPDFLinkOpen}>
            {pdfLabel}
          </a>
          {unitLabel}
        </span>
      </div>
    </div>
  );
};
